import { Box, Divider, Skeleton, Stack, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Badge, Icon, Text } from '@repo/foundations';
import { noop } from 'lodash-es';
import { useMemo, useState } from 'react';
import type { Monitor } from '../../api';
import { useBulkUpdateMonitors } from '../../api';
import { useExtendedUserList } from '../../api/hooks/user/useExtendedUserList';
import { getOwnerAndGroupSelectorOptions } from '../../components/EntityPageLayout/utils';
import SearchBox from '../../components/SearchBox/SearchBox';
import { ActionCheckbox } from '../../components/TableV2/ActionCheckbox';

interface BulkEditOwnersModalProps {
	onClose: () => void;
	monitors: Monitor[];
}

export default function BulkEditOwnersModal({
	onClose,
	monitors,
}: BulkEditOwnersModalProps) {
	const { mutateAsync: bulkUpdateMonitors } = useBulkUpdateMonitors({});
	const [searchTerm, setSearchTerm] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const { activeUsers, disabledUsers, userGroups } = useExtendedUserList();
	const theme = useMantineTheme();

	const ownerOptions = useMemo(
		() =>
			getOwnerAndGroupSelectorOptions(
				[...(disabledUsers ?? []), ...(activeUsers ?? [])],
				userGroups ?? [],
				noop,
				noop
			),
		[activeUsers, disabledUsers, userGroups]
	);

	const filteredOwnerOptions = useMemo(() => {
		if (!searchTerm) {
			return ownerOptions;
		}

		return ownerOptions.filter((owner) =>
			owner.label.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [ownerOptions, searchTerm]);

	const handleOwnerToggle = async (value: string | boolean) => {
		const selectedValue = String(value);
		const selectedOption = ownerOptions.find(
			(owner) => owner.value === selectedValue
		);

		setSubmitting(true);
		try {
			const updates: Record<string, Record<string, unknown>> = {};

			monitors.forEach((monitor) => {
				updates[monitor.id] = {
					owners: selectedOption?.group === 'groups' ? [] : [selectedValue],
					owners_groups:
						selectedOption?.group === 'groups' ? [selectedValue] : [],
				};
			});

			await bulkUpdateMonitors({
				body: {
					updates,
				},
			});

			showNotification({
				title: 'Owners updated',
				message: `Owners updated for ${monitors.length} monitor${monitors.length > 1 ? 's' : ''}.`,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
			onClose();
		} catch (e) {
			showNotification({
				title: 'Error',
				message: 'Failed to update owners for selected monitors.',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Box m={`-${theme.spacing.sm}`}>
			<Badge
				ml={theme.spacing.sm}
				mb={0}
				variant="neutral"
				size="sm"
				sx={{ textTransform: 'lowercase' }}
			>
				{monitors.length} monitor{monitors.length > 1 ? 's' : ''}
			</Badge>
			<SearchBox
				size="md"
				icon={undefined}
				className={undefined}
				classNames={undefined}
				variant="default"
				placeholder="Search..."
				onlySearchOnEnter={false}
				onSearch={setSearchTerm}
				onCancelSearch={() => setSearchTerm('')}
				defaultSearchTerm=""
				sx={{
					input: {
						boxShadow: 'none',
						border: 'none',
					},
					'input:focus': {
						border: 'none',
						boxShadow: 'none',
					},
				}}
			/>
			<Divider mx={`-${theme.spacing.md}`} />
			<Stack mx={`${-theme.spacing.sm}`} mt={theme.spacing.xs} spacing={0}>
				{submitting && <Skeleton h={theme.other.space[80]} />}
				{!submitting && filteredOwnerOptions.length === 0 && (
					<Box py="xs" px="sm">
						<Text size="sm" color="text/secondary/default">
							No owners found
						</Text>
					</Box>
				)}
				{!submitting &&
					filteredOwnerOptions.map((owner) => (
						<ActionCheckbox
							key={`${owner.label}-${owner.value}`}
							handleTrigger={handleOwnerToggle}
							iconType="avatar"
							item={owner}
							state="unchecked"
						/>
					))}
			</Stack>
		</Box>
	);
}
